/**
 * BulkCapByTagModal — Push 70b (D-198 wallet collapse, 2026-07-17).
 *
 * Admin picks a tag; the modal previews every user carrying that tag and
 * lets the admin set a monthly spend cap for all of them in one shot.
 * Two application shapes:
 *
 *   - Uniform cap: type one dollar amount; every affected user gets the
 *     same cap in credits.
 *   - Split evenly: type a group budget; the modal computes per-user
 *     cap = round(budget / affected_users).
 *
 * A preview table below lets the admin fine-tune individual caps before
 * committing. On Confirm, the modal fires N sequential PATCH calls to
 * /api/admin/people/[id]/spend-cap. Each PATCH emits a `user_cap_changed`
 * event log row via the endpoint (server-side).
 *
 * Discipline note from the plan doc: tags don't enforce a group budget.
 * Every user gets their own per-user cap; they all draw from the shared
 * workspace credit pot until they individually hit their ceiling. The
 * modal surfaces that framing explicitly to prevent the "shared budget"
 * misread.
 *
 * Style DNA: .modal-card / .modal-title / .modal-footer per Push 55 +
 * Push 68 composer discipline. Width ~560px to accommodate the preview
 * table; scroll body when the affected-user list overflows.
 */

import { Button } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';

import { ApiError, type AdminPerson } from '../../lib/api.ts';
import { useToast } from '../../components/Toast.tsx';

export interface BulkCapByTagModalProps {
  /** Full members roster from the parent People list. */
  members: AdminPerson[];
  /** Distinct tag values in the account (from /api/admin/account/tags). */
  accountTags: string[];
  onClose: () => void;
  onSaved: () => void;
}

type CapMode = 'uniform' | 'split';

// Match server-side sanitize: NUMERIC on the wire as a string. UI accepts
// integers (whole credits) — matches the existing spend-cap endpoint
// where fractional credits aren't meaningful for a monthly cap.
function sanitizeInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

export function BulkCapByTagModal({
  members,
  accountTags,
  onClose,
  onSaved,
}: BulkCapByTagModalProps) {
  const { showToast } = useToast();
  const [selectedTag, setSelectedTag] = useState<string>(
    accountTags[0] ?? '',
  );
  const [mode, setMode] = useState<CapMode>('uniform');
  const [uniformInput, setUniformInput] = useState<string>('');
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [perUserOverrides, setPerUserOverrides] = useState<
    Record<string, string>
  >({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  // Reset per-user overrides + inputs whenever the tag flips.
  useEffect(() => {
    setPerUserOverrides({});
  }, [selectedTag]);

  const affected = useMemo(() => {
    if (selectedTag === '') return [];
    const key = selectedTag.toLowerCase();
    return members.filter(
      (m) =>
        m.is_active &&
        (m.tags ?? []).some((t) => t.toLowerCase() === key),
    );
  }, [members, selectedTag]);

  const derivedCapCredits = useMemo(() => {
    if (mode === 'uniform') {
      return sanitizeInt(uniformInput);
    }
    const budget = sanitizeInt(budgetInput);
    if (budget === null || affected.length === 0) return null;
    return Math.floor(budget / affected.length);
  }, [mode, uniformInput, budgetInput, affected.length]);

  function effectiveCapFor(userId: string): number | null {
    const override = perUserOverrides[userId];
    if (override !== undefined) {
      return sanitizeInt(override);
    }
    return derivedCapCredits;
  }

  async function handleConfirm() {
    if (selectedTag === '') return;
    if (affected.length === 0) return;
    // Every affected user must have a valid cap resolved (either the
    // derived global or an override). Nulls signal "invalid input";
    // bail before firing any PATCH.
    for (const m of affected) {
      const cap = effectiveCapFor(m.account_user_id);
      if (cap === null) {
        showToast({
          kind: 'error',
          message: `Missing or invalid cap for ${m.display_name}.`,
        });
        return;
      }
    }
    setBusy(true);
    setProgress({ done: 0, total: affected.length });
    const failed: string[] = [];
    for (let i = 0; i < affected.length; i++) {
      const m = affected[i]!;
      const cap = effectiveCapFor(m.account_user_id)!;
      try {
        const resp = await fetch(
          `/api/admin/people/${encodeURIComponent(m.account_user_id)}/spend-cap`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credits: cap }),
          },
        );
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as {
            message?: string;
          };
          throw new ApiError(
            resp.status,
            body.message ?? `Cap update failed: ${resp.status}`,
          );
        }
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'failed';
        failed.push(`${m.display_name} (${msg})`);
      }
      setProgress({ done: i + 1, total: affected.length });
    }
    setBusy(false);
    setProgress(null);
    if (failed.length === 0) {
      showToast({
        kind: 'success',
        message: `Cap set for ${affected.length} tagged ${affected.length === 1 ? 'user' : 'users'}.`,
      });
      onSaved();
    } else if (failed.length === affected.length) {
      showToast({
        kind: 'error',
        message: `All ${affected.length} cap updates failed. Examples: ${failed.slice(0, 2).join('; ')}.`,
      });
    } else {
      showToast({
        kind: 'error',
        message: `${affected.length - failed.length} of ${affected.length} succeeded. Failed: ${failed.join('; ')}.`,
      });
      onSaved();
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Set spend caps by tag"
      className="modal-backdrop"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 620, width: '100%' }}
      >
        <div className="modal-header">
          <h2 className="modal-title">Set spend caps by tag</h2>
          <button
            type="button"
            className="modal-close"
            onClick={() => {
              if (!busy) onClose();
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <p
            className="muted"
            style={{ fontSize: 13, margin: '0 0 12px', lineHeight: 1.55 }}
          >
            Pick a tag, set a spend cap, and every user carrying that tag
            gets their monthly cap updated. Tags don't share a budget —
            each user gets their own cap on the shared workspace credit
            pot. When a user hits their cap, they stop; everyone else on
            the tag keeps drawing until they hit theirs.
          </p>

          <label
            style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}
          >
            <span className="form-label" style={{ fontSize: 12 }}>
              Tag
            </span>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="input"
              disabled={busy || accountTags.length === 0}
              style={{ maxWidth: 260 }}
            >
              {accountTags.length === 0 ? (
                <option value="">No tags in this workspace yet</option>
              ) : (
                accountTags.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))
              )}
            </select>
          </label>

          {selectedTag !== '' && (
            <div style={{ marginBottom: 12, fontSize: 12 }} className="muted">
              {affected.length === 0
                ? `No active users are tagged '${selectedTag}' yet.`
                : `${affected.length} active ${affected.length === 1 ? 'user carries' : 'users carry'} '${selectedTag}'.`}
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="radio"
                name="cap-mode"
                value="uniform"
                checked={mode === 'uniform'}
                onChange={() => setMode('uniform')}
                disabled={busy}
              />
              Same cap for each user
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="radio"
                name="cap-mode"
                value="split"
                checked={mode === 'split'}
                onChange={() => setMode('split')}
                disabled={busy}
              />
              Split a group budget evenly
            </label>
          </div>

          {mode === 'uniform' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              <span className="form-label" style={{ fontSize: 12 }}>
                Monthly cap per user (credits)
              </span>
              <input
                type="number"
                min="0"
                step="1"
                value={uniformInput}
                onChange={(e) => setUniformInput(e.target.value)}
                className="input"
                placeholder="e.g. 1000"
                disabled={busy}
                style={{ maxWidth: 220 }}
              />
            </label>
          ) : (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              <span className="form-label" style={{ fontSize: 12 }}>
                Total group budget (credits)
              </span>
              <input
                type="number"
                min="0"
                step="1"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="input"
                placeholder="e.g. 10000"
                disabled={busy}
                style={{ maxWidth: 220 }}
              />
              {derivedCapCredits !== null && affected.length > 0 && (
                <span className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Splits to {derivedCapCredits.toLocaleString()} credits per user
                  ({affected.length} users).
                </span>
              )}
            </label>
          )}

          {affected.length > 0 && derivedCapCredits !== null && (
            <div style={{ marginTop: 4, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div
                className="form-label"
                style={{ fontSize: 12, marginBottom: 6 }}
              >
                Preview {affected.length === 1 ? '(1 user)' : `(${affected.length} users)`}
              </div>
              <div className="table-wrap">
                <table className="table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th style={{ width: 160 }}>Cap (credits)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affected.map((m) => {
                      const override = perUserOverrides[m.account_user_id];
                      const value =
                        override !== undefined
                          ? override
                          : String(derivedCapCredits);
                      return (
                        <tr key={m.account_user_id}>
                          <td>{m.display_name}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={value}
                              onChange={(e) => {
                                const next = e.target.value;
                                setPerUserOverrides((prev) => ({
                                  ...prev,
                                  [m.account_user_id]: next,
                                }));
                              }}
                              className="input input-sm"
                              disabled={busy}
                              aria-label={`Cap for ${m.display_name}`}
                              style={{ width: 140 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {progress !== null && (
            <p
              className="muted"
              aria-live="polite"
              style={{ fontSize: 12, margin: '10px 0 0' }}
            >
              Updating caps… {progress.done} of {progress.total} done.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            loading={busy}
            aria-busy={busy || undefined}
            disabled={
              busy ||
              selectedTag === '' ||
              affected.length === 0 ||
              derivedCapCredits === null
            }
            onClick={() => void handleConfirm()}
          >
            {busy
              ? 'Applying…'
              : `Apply cap to ${affected.length} ${affected.length === 1 ? 'user' : 'users'}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
